import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import {
  FileText, CheckCircle, XCircle, Clock, Warning, Fire, TrendUp, CurrencyInr,
  Users, ArrowRight, ChartBar, Upload,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const ACCENT = ["#4f46e5", "#059669", "#d97706", "#dc2626", "#0891b2", "#7c3aed", "#db2777", "#65a30d"];

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics/applications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-slate-500 text-sm">Loading analytics…</div>;

  const s = data.summary;
  const cards = [
    { label: "Total Applications", value: s.total, icon: FileText, accent: "indigo" },
    { label: "Open", value: s.open, icon: Clock, accent: "amber" },
    { label: "Completed", value: s.completed, icon: CheckCircle, accent: "emerald" },
    { label: "Rejected", value: s.rejected, icon: XCircle, accent: "rose" },
    { label: "Pending", value: s.pending, icon: Clock, accent: "orange" },
    { label: "Urgent", value: s.urgent, icon: Warning, accent: "amber" },
    { label: "Emergency", value: s.emergency, icon: Fire, accent: "rose" },
    { label: "Today", value: s.today, icon: TrendUp, accent: "indigo" },
    { label: "Today's Revenue", value: inr(s.today_revenue), icon: CurrencyInr, accent: "emerald" },
    { label: "Total Revenue", value: inr(s.total_revenue), icon: CurrencyInr, accent: "violet" },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-indigo-600 font-semibold mb-2 flex items-center gap-2">
            <ChartBar size={14} weight="fill" /> Analytics
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Application Reports</h1>
          <p className="text-sm text-slate-600 mt-1.5">Service-wise, operator-wise, day/month trends & revenue insights.</p>
        </div>
        <Link to="/data-import">
          <Button variant="outline" className="gap-2" data-testid="reports-import-btn">
            <Upload size={16} weight="bold" /> Import Old Data
          </Button>
        </Link>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((c) => (
          <Card key={c.label} className="p-3 bg-white border-slate-200" data-testid={`rep-card-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{c.label}</div>
                <div className="text-xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "var(--font-heading)" }}>{c.value}</div>
              </div>
              <c.icon size={20} weight="fill" className={`text-${c.accent}-400`} />
            </div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="trend" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="trend" data-testid="rep-tab-trend">Trends</TabsTrigger>
          <TabsTrigger value="service" data-testid="rep-tab-service">By Service</TabsTrigger>
          <TabsTrigger value="operator" data-testid="rep-tab-operator">By Operator</TabsTrigger>
          <TabsTrigger value="status" data-testid="rep-tab-status">By Status</TabsTrigger>
        </TabsList>

        {/* Trend */}
        <TabsContent value="trend" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4 bg-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Applications by Day (last 30)</div>
            {data.by_day.length === 0 ? (
              <EmptyChart />
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={data.by_day} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                    <YAxis fontSize={10} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card className="p-4 bg-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Revenue by Month (last 12)</div>
            {data.by_month.length === 0 ? (
              <EmptyChart />
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={data.by_month} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" fontSize={10} />
                    <YAxis fontSize={10} />
                    <Tooltip formatter={(v) => inr(v)} />
                    <Bar dataKey="revenue" fill="#059669" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card className="p-4 bg-white lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
              <Users size={12} weight="fill" /> Customer Growth (last 12 months)
            </div>
            {data.customer_growth.length === 0 ? (
              <EmptyChart />
            ) : (
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <LineChart data={data.customer_growth} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" fontSize={10} />
                    <YAxis fontSize={10} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#d97706" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Service */}
        <TabsContent value="service">
          <Card className="p-4 bg-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Top Services</div>
            {data.by_service.length === 0 ? <EmptyChart /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2">Service</th>
                      <th className="text-right py-2 pr-3">Total</th>
                      <th className="text-right py-2 pr-3">Open</th>
                      <th className="text-right py-2 pr-3">Completed</th>
                      <th className="text-right py-2">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_service.map((s) => (
                      <tr key={s.service} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 font-medium text-slate-800">{s.service}</td>
                        <td className="py-2 pr-3 text-right">{s.total}</td>
                        <td className="py-2 pr-3 text-right text-amber-700">{s.open}</td>
                        <td className="py-2 pr-3 text-right text-emerald-700">{s.completed}</td>
                        <td className="py-2 text-right font-semibold">{inr(s.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Operator */}
        <TabsContent value="operator">
          <Card className="p-4 bg-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Operator Performance</div>
            {data.by_operator.length === 0 ? <EmptyChart /> : (
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={data.by_operator} layout="vertical" margin={{ top: 5, right: 10, left: 60, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" fontSize={10} allowDecimals={false} />
                    <YAxis dataKey="operator" type="category" fontSize={10} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="completed" fill="#059669" name="Completed" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="total" fill="#94a3b8" name="Total" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Status pie */}
        <TabsContent value="status">
          <Card className="p-4 bg-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Applications by Status</div>
            {data.by_status.length === 0 ? <EmptyChart /> : (
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={110} label>
                      {data.by_status.map((_, i) => <Cell key={i} fill={ACCENT[i % ACCENT.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-56 flex items-center justify-center text-sm text-slate-400 flex-col gap-2">
      <ChartBar size={28} weight="thin" />
      No data yet. As applications get created & completed, charts will populate here.
      <Link to="/data-import" className="text-indigo-600 hover:underline text-xs mt-1 inline-flex items-center gap-1">
        Import old data <ArrowRight size={11} weight="bold" />
      </Link>
    </div>
  );
}
